import { Header } from '@/components/layout/Header'
import { Footer } from '@/components/layout/Footer'
import { SEO, StructuredData } from '@/components/seo'

export default function ContactPage() {
  return (
    <div className="min-h-screen bg-surface-950 text-white">
      <SEO
        title="Contact Us - Sales, Support & Partnerships"
        description="Get in touch with JumpstoneHR. Contact our sales team for demos, support team for help, or explore partnership opportunities. We'd love to hear from you."
        canonical="/contact"
        structuredData={[
          StructuredData.webPage(
            'Contact JumpstoneHR',
            'Get in touch with our sales, support, partnerships, or press teams.',
            '/contact'
          ),
          StructuredData.breadcrumb([
            { name: 'Home', url: '/' },
            { name: 'Contact', url: '/contact' },
          ]),
          StructuredData.localBusiness(),
          {
            '@context': 'https://schema.org',
            '@type': 'ContactPage',
            name: 'Contact JumpstoneHR',
            description: 'Contact page for JumpstoneHR - reach our sales, support, partnerships, or press teams.',
            mainEntity: {
              '@type': 'Organization',
              name: 'Jumpstone Technology Inc.',
              email: 'hello@jumpstonehr.com',
              contactPoint: [
                {
                  '@type': 'ContactPoint',
                  contactType: 'sales',
                  email: 'sales@jumpstonehr.com',
                  availableLanguage: 'English',
                },
                {
                  '@type': 'ContactPoint',
                  contactType: 'customer support',
                  email: 'support@jumpstonehr.com',
                  availableLanguage: 'English',
                },
                {
                  '@type': 'ContactPoint',
                  contactType: 'partnerships',
                  email: 'partnerships@jumpstonehr.com',
                },
                {
                  '@type': 'ContactPoint',
                  contactType: 'press',
                  email: 'press@jumpstonehr.com',
                },
              ],
            },
          },
        ]}
      />
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative pt-32 pb-16 overflow-hidden">
          <div className="absolute inset-0 opacity-[0.03]">
            <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
              <defs>
                <pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse">
                  <path d="M 10 0 L 0 0 0 10" fill="none" stroke="white" strokeWidth="0.5"/>
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#grid)" />
            </svg>
          </div>
          
          {/* Gradient orb */}
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-bl from-brand-500/20 via-brand-600/10 to-transparent rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" />
          
          <div className="relative mx-auto max-w-4xl section-padding">
            <div className="flex items-center gap-3 mb-8">
              <div className="h-px flex-1 max-w-[60px] bg-brand-500" />
              <span className="text-brand-400 text-sm font-medium tracking-widest uppercase">Get in Touch</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] mb-6">
              Let's talk about{' '}
              <span className="text-brand-400">hiring</span>
            </h1>
            
            <p className="text-xl text-surface-300 max-w-2xl">
              Have questions about JumpstoneHR? Want to see a demo? We'd love to hear from you.
            </p>
          </div>
        </section>

        {/* Contact Options */}
        <section className="pb-24">
          <div className="mx-auto max-w-4xl section-padding">
            <div className="grid md:grid-cols-2 gap-8">
              {/* Sales */}
              <div className="bg-surface-900 border border-surface-800 rounded-2xl p-8 hover:border-brand-500/30 transition-colors">
                <div className="w-14 h-14 rounded-xl bg-brand-500/10 flex items-center justify-center mb-6">
                  <svg className="w-7 h-7 text-brand-400" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 01-.825-.242m9.345-8.334a2.126 2.126 0 00-.476-.095 48.64 48.64 0 00-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0011.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.577.075 1.157.14 1.74.194V21l4.155-4.155" />
                  </svg>
                </div>
                <h2 className="text-xl font-semibold text-white mb-3">Sales Inquiries</h2>
                <p className="text-surface-400 mb-6">
                  Interested in JumpstoneHR for your organization? Let's discuss how we can help transform your hiring process.
                </p>
                <a 
                  href="mailto:sales@jumpstonehr.com" 
                  className="inline-flex items-center gap-2 text-brand-400 hover:text-brand-300 font-medium transition-colors"
                >
                  sales@jumpstonehr.com
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                  </svg>
                </a>
              </div>

              {/* Support */}
              <div className="bg-surface-900 border border-surface-800 rounded-2xl p-8 hover:border-brand-500/30 transition-colors">
                <div className="w-14 h-14 rounded-xl bg-brand-500/10 flex items-center justify-center mb-6">
                  <svg className="w-7 h-7 text-brand-400" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
                  </svg>
                </div>
                <h2 className="text-xl font-semibold text-white mb-3">Customer Support</h2>
                <p className="text-surface-400 mb-6">
                  Already a customer? Our support team is here to help you get the most out of JumpstoneHR.
                </p>
                <a 
                  href="mailto:support@jumpstonehr.com" 
                  className="inline-flex items-center gap-2 text-brand-400 hover:text-brand-300 font-medium transition-colors"
                >
                  support@jumpstonehr.com
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                  </svg>
                </a>
              </div>

              {/* Partnerships */}
              <div className="bg-surface-900 border border-surface-800 rounded-2xl p-8 hover:border-brand-500/30 transition-colors">
                <div className="w-14 h-14 rounded-xl bg-brand-500/10 flex items-center justify-center mb-6">
                  <svg className="w-7 h-7 text-brand-400" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
                  </svg>
                </div>
                <h2 className="text-xl font-semibold text-white mb-3">Partnerships</h2>
                <p className="text-surface-400 mb-6">
                  Interested in integrating with JumpstoneHR or exploring partnership opportunities? Let's connect.
                </p>
                <a 
                  href="mailto:partnerships@jumpstonehr.com" 
                  className="inline-flex items-center gap-2 text-brand-400 hover:text-brand-300 font-medium transition-colors"
                >
                  partnerships@jumpstonehr.com
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                  </svg>
                </a>
              </div>

              {/* Press */}
              <div className="bg-surface-900 border border-surface-800 rounded-2xl p-8 hover:border-brand-500/30 transition-colors">
                <div className="w-14 h-14 rounded-xl bg-brand-500/10 flex items-center justify-center mb-6">
                  <svg className="w-7 h-7 text-brand-400" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 7.5h1.5m-1.5 3h1.5m-7.5 3h7.5m-7.5 3h7.5m3-9h3.375c.621 0 1.125.504 1.125 1.125V18a2.25 2.25 0 01-2.25 2.25M16.5 7.5V18a2.25 2.25 0 002.25 2.25M16.5 7.5V4.875c0-.621-.504-1.125-1.125-1.125H4.125C3.504 3.75 3 4.254 3 4.875V18a2.25 2.25 0 002.25 2.25h13.5M6 7.5h3v3H6v-3z" />
                  </svg>
                </div>
                <h2 className="text-xl font-semibold text-white mb-3">Press & Media</h2>
                <p className="text-surface-400 mb-6">
                  For press inquiries, interviews, or media resources, reach out to our communications team.
                </p>
                <a 
                  href="mailto:press@jumpstonehr.com" 
                  className="inline-flex items-center gap-2 text-brand-400 hover:text-brand-300 font-medium transition-colors"
                >
                  press@jumpstonehr.com
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                  </svg>
                </a>
              </div>
            </div>

          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  )
}

